import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import {
  BrandThreadItem,
  BrandThreadTimeline,
  adminGetBrandThreadByDeal,
  adminGetBrandThreadTimeline,
  getBrandThreadByDeal,
  getBrandThreadTimeline,
  markBrandThreadRead,
  sendBrandThreadMessage,
} from '../../api/payment/brand-thread.api';
import { routePaths } from '../../routes/routes-path';
import { useBrandThreadUnread } from '../../hooks/use-brand-thread-unread';
import {
  THREAD_CLOSED_NOTICE,
  THREAD_CONTACT_WARNING,
  threadFairnessNote,
  THREAD_POLL_MS,
  THREAD_READ_EVENT,
  THREAD_TEXT_MAX_LENGTH,
  hasNewFromOthers,
  mergeTimeline,
  revisionCounterLabel,
  threadCounterpartLabel,
  threadDealStatusLabel,
  threadEventLabel,
  validateThreadText,
} from '../../utils/brand-thread';
import { directEditCounterLabel } from '../../utils/brand-deal-content';

/**
 * Khung "Trao đổi" dùng chung (spec 2026-10-12 §3.1): tin nhắn hai bên trộn với dòng sự kiện, tin mới bám đáy, hỏi lại
 * 10 giây một lần khi tab đang hiện, đánh dấu đã đọc khi mở và khi có tin mới của bên kia. mode="admin" chỉ đọc.
 * Mở theo `threadId`, hoặc theo `dealId` (tự tìm phòng).
 */

const errorMessage = (err: unknown, fallback: string) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message || fallback;

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit', hour12: false });

// Khai ở cấp module (landmine: component khai trong render bị remount, mất vị trí cuộn và chữ đang gõ).
const EventLine: React.FC<{ item: BrandThreadItem }> = ({ item }) => (
  <div className="flex flex-col items-center gap-0.5 py-1 text-center text-[12px] text-slate-500 dark:text-slate-400">
    <span>
      {threadEventLabel(item)} · {formatTime(item.at)}
    </span>
    {item.note && <span className="max-w-[90%] whitespace-pre-wrap">{item.note}</span>}
  </div>
);

const MessageBubble: React.FC<{ item: BrandThreadItem; showSender: boolean }> = ({ item, showSender }) => (
  <div className={`flex flex-col gap-0.5 ${item.mine ? 'items-end' : 'items-start'}`}>
    {showSender && <span className="text-[11px] ui-muted">{item.senderRole === 'Creator' ? 'Creator' : 'Người đặt'}</span>}
    <div
      className={`max-w-[85%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-[14px] ${
        item.mine ? 'bg-[#0057FF] text-white' : 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-100'
      }`}
    >
      {item.text}
    </div>
    <span className="text-[11px] ui-muted">{formatTime(item.at)}</span>
    {item.contactFlag && (
      <span className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">Có thông tin liên hệ</span>
    )}
  </div>
);

export interface BrandThreadPanelProps {
  threadId?: string | null;
  dealId?: string | null;
  mode?: 'member' | 'admin';
  /** Hiện tên chiến dịch, bên kia và trạng thái từng đơn ở đầu khung (trang làm việc gọn, admin). */
  showHeader?: boolean;
}

const BrandThreadPanel: React.FC<BrandThreadPanelProps> = ({ threadId: threadIdProp, dealId, mode = 'member', showHeader = false }) => {
  const isAdmin = mode === 'admin';
  const [threadId, setThreadId] = useState<string | null>(threadIdProp ?? null);
  const [timeline, setTimeline] = useState<BrandThreadTimeline | null>(null);
  const [items, setItems] = useState<BrandThreadItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [warning, setWarning] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef(true);
  const itemsRef = useRef<BrandThreadItem[]>([]);
  itemsRef.current = items;

  // Có threadId thì dùng luôn; chỉ có dealId thì hỏi phòng của đơn.
  useEffect(() => {
    if (threadIdProp) {
      setThreadId(threadIdProp);
      return undefined;
    }
    if (!dealId) return undefined;
    let cancelled = false;
    (isAdmin ? adminGetBrandThreadByDeal(dealId) : getBrandThreadByDeal(dealId))
      .then((ref) => {
        if (!cancelled) setThreadId(ref.threadId);
      })
      .catch((err) => {
        if (cancelled) return;
        setLoading(false);
        toast.error(errorMessage(err, 'Không mở được phòng trao đổi.'));
      });
    return () => {
      cancelled = true;
    };
  }, [threadIdProp, dealId, isAdmin]);

  const fetchLatest = useCallback((): Promise<BrandThreadTimeline | null> => {
    if (!threadId) return Promise.resolve(null);
    return isAdmin ? adminGetBrandThreadTimeline(threadId) : getBrandThreadTimeline(threadId);
  }, [threadId, isAdmin]);

  const markRead = useCallback(() => {
    if (!threadId || isAdmin) return;
    markBrandThreadRead(threadId)
      .then(() => window.dispatchEvent(new Event(THREAD_READ_EVENT)))
      .catch(() => undefined);
  }, [threadId, isAdmin]);

  // Lần tải đầu.
  useEffect(() => {
    if (!threadId) return undefined;
    let cancelled = false;
    setLoading(true);
    fetchLatest()
      .then((t) => {
        if (cancelled || !t) return;
        stickToBottomRef.current = true;
        setTimeline(t);
        setItems(t.items);
        setHasMore(t.hasMore);
        markRead();
      })
      .catch((err) => {
        if (!cancelled) toast.error(errorMessage(err, 'Không tải được phòng trao đổi.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [threadId, fetchLatest, markRead]);

  // Hỏi lại 10 giây một lần, chỉ khi tab đang hiện; quay lại tab thì hỏi ngay.
  useEffect(() => {
    if (!threadId) return undefined;
    const tick = () => {
      if (document.visibilityState !== 'visible') return;
      fetchLatest()
        .then((t) => {
          if (!t) return;
          const prev = itemsRef.current;
          setTimeline(t);
          setItems(mergeTimeline(prev, t.items));
          if (hasNewFromOthers(prev, t.items)) markRead();
        })
        .catch(() => undefined);
    };
    const timer = setInterval(tick, THREAD_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [threadId, fetchLatest, markRead]);

  // Tin mới bám đáy khi người xem đang ở đáy.
  useEffect(() => {
    const el = listRef.current;
    if (el && stickToBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [items]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
  };

  const loadOlder = async () => {
    if (!threadId || items.length === 0) return;
    const oldest = items[0];
    const cursor = { before: oldest.at, beforeKey: oldest.key };
    setLoadingOlder(true);
    stickToBottomRef.current = false;
    try {
      const t = await (isAdmin ? adminGetBrandThreadTimeline(threadId, cursor) : getBrandThreadTimeline(threadId, cursor));
      setItems((prev) => mergeTimeline(prev, t.items));
      setHasMore(t.hasMore);
    } catch (err) {
      toast.error(errorMessage(err, 'Không tải được tin cũ hơn.'));
    } finally {
      setLoadingOlder(false);
    }
  };

  const send = async () => {
    if (!threadId) return;
    const error = validateThreadText(text);
    if (error) {
      toast.error(error);
      return;
    }
    setSending(true);
    try {
      const item = await sendBrandThreadMessage(threadId, text.trim());
      stickToBottomRef.current = true;
      // Đặt cảnh báo trước khi thêm tin (React 17 không gộp setState sau await).
      setWarning(item.contactFlag);
      setText('');
      setItems((prev) => mergeTimeline(prev, [item]));
    } catch (err) {
      toast.error(errorMessage(err, 'Không gửi được tin nhắn.'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="ui-card flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="ui-heading text-[15px] font-bold">Trao đổi</h2>
        {timeline?.revision && (
          <span className="text-[13px] font-semibold text-amber-600 dark:text-amber-400">{revisionCounterLabel(timeline.revision)}</span>
        )}
        {timeline?.directEdit && (
          <span className="text-[13px] font-semibold text-amber-600 dark:text-amber-400">
            {directEditCounterLabel(timeline.directEdit.count, timeline.directEdit.max)}
          </span>
        )}
      </div>

      {showHeader && timeline && (
        <div className="flex flex-col gap-0.5 text-[13px]">
          <span className="ui-text font-semibold">{timeline.campaignTitle}</span>
          <span className="ui-muted">{threadCounterpartLabel(timeline)}</span>
          {timeline.deals.map((d) => (
            <span key={d.dealId} className="ui-muted">
              {d.kind === 'Live' ? 'Quảng cáo livestream' : 'Đăng bài'}: {threadDealStatusLabel(d.status)}
            </span>
          ))}
        </div>
      )}

      <div ref={listRef} onScroll={onScroll} className="flex max-h-[420px] min-h-[160px] flex-col gap-2 overflow-y-auto pr-1">
        {hasMore && (
          <button type="button" disabled={loadingOlder} onClick={loadOlder} className="self-center text-[12px] font-semibold text-[#0057FF]">
            {loadingOlder ? 'Đang tải…' : 'Xem tin cũ hơn'}
          </button>
        )}
        {loading ? (
          <div className="flex justify-center p-6">
            <Loader2 className="animate-spin ui-muted" size={20} />
          </div>
        ) : items.length === 0 ? (
          <p className="ui-muted p-4 text-center text-[13px]">Chưa có tin nhắn nào.</p>
        ) : (
          items.map((item) =>
            item.type === 'event' ? (
              <EventLine key={item.key} item={item} />
            ) : (
              <MessageBubble key={item.key} item={item} showSender={isAdmin} />
            )
          )
        )}
      </div>

      {!isAdmin &&
        timeline &&
        (timeline.canWrite ? (
          <div className="flex flex-col gap-1.5">
            {warning && (
              <p className="rounded-lg bg-amber-50 p-2.5 text-[12px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                {THREAD_CONTACT_WARNING}
              </p>
            )}
            <textarea
              aria-label="Tin nhắn"
              value={text}
              maxLength={THREAD_TEXT_MAX_LENGTH}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              placeholder="Nhập tin nhắn…"
              className="w-full resize-none rounded-md border ui-divider bg-transparent px-3 py-2 text-[14px] outline-none ui-text"
            />
            <div className="flex items-start justify-between gap-3 text-[12px] ui-muted">
              <span>{threadFairnessNote(!!timeline?.directEdit)}</span>
              <span className="shrink-0">
                {text.length}/{THREAD_TEXT_MAX_LENGTH}
              </span>
            </div>
            <button
              type="button"
              disabled={sending || text.trim().length === 0}
              onClick={send}
              className="self-end rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
            >
              {sending ? 'Đang gửi…' : 'Gửi'}
            </button>
          </div>
        ) : (
          <p className="rounded-lg bg-slate-50 p-2.5 text-center text-[13px] ui-muted dark:bg-slate-800">{THREAD_CLOSED_NOTICE}</p>
        ))}
    </div>
  );
};

/** Nút "Trao đổi" mở/đóng khung ngay tại dòng (deal, ứng tuyển, đơn đặt quảng cáo); chấm đỏ khi phòng có tin chưa đọc. */
export const BrandThreadToggle: React.FC<{ threadId?: string | null; dealId?: string | null }> = ({ threadId, dealId }) => {
  const [open, setOpen] = useState(false);
  const unread = useBrandThreadUnread();
  if (!threadId && !dealId) return null;
  const hasUnread = !!threadId && unread.threadIds.includes(threadId);
  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative self-start rounded-md border ui-divider px-3 py-1.5 text-[13px] font-semibold ui-text"
      >
        {open ? 'Ẩn trao đổi' : 'Trao đổi'}
        {hasUnread && <span aria-label="Có tin nhắn mới" className="absolute -right-1 -top-1 size-2.5 rounded-full bg-rose-500" />}
      </button>
      {open && <BrandThreadPanel threadId={threadId} dealId={threadId ? null : dealId} />}
    </div>
  );
};

/** Link sang trang làm việc của đơn (phía creator); chấm đỏ khi phòng có tin chưa đọc. */
export const BrandThreadLink: React.FC<{ dealId: string; threadId?: string | null }> = ({ dealId, threadId }) => {
  const unread = useBrandThreadUnread();
  const hasUnread = !!threadId && unread.threadIds.includes(threadId);
  return (
    <Link to={`${routePaths.brandDeals}/task/${dealId}`} className="relative font-semibold text-[#0057FF]">
      Trao đổi
      {hasUnread && <span aria-label="Có tin nhắn mới" className="absolute -right-2.5 -top-0.5 size-2 rounded-full bg-rose-500" />}
    </Link>
  );
};

export default BrandThreadPanel;
